/* eslint-disable @typescript-eslint/no-unused-vars */
import { lazy, ReactElement, Suspense } from "react";
import { Container } from "react-bootstrap";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import HttpStatusCode from "../common/httpStatusCode";
import getProductInfo from "../common/productInfo";

import Footer from "./footer/footer";
import Header from "./Header/Header";
import RouteConsts from "./RouteConsts";

import LoadingPage from "../views/loading-page";

import "./layout.scss";

import { TrueLayerProviderName } from "../models/paymentProviders/trueLayer/trueLayerModels";
import { TotalProcessingProviderName } from "../models/paymentProviders/totalProcessing/total-processing-provider";
import IntentToPayServiceProvider from "../context/intentToPay/IntentToPayProvider";
import PaymentRequestACHDebitProcessingPage from "../views/PaymentRequest/payment-request-ach-debit-processing-page";

const HealthPage = lazy(() => import("../views/health-page"));
const HomePage = lazy(() => import("../views/Home/HomePage"));
const PreviewPage = lazy(() => import("../views/preview/preview-page"));
const TestPage = lazy(() => import("../views/Test/test-page"));
const ErrorPage = lazy(() => import("./Errors/ErrorPage"));

const RequestLinkPage = lazy(() => import("../views/request-link/request-link-page"));
const PaymentRequestPage = lazy(() => import("../views/PaymentRequest/payment-request-page"));

const PaymentWaitingPage = lazy(() => import("../views/Payments/payment-waiting-page"));
const PaymentCompletePage = lazy(() => import("../views/Payments/payment-complete-page"));
const PaymentReservedPage = lazy(() => import("../views/Payments/PaymentReservedPage"));
const PaymentProcessingPage = lazy(() => import("../views/Payments/payment-processing-page"));
const PaymentBankDebitScheduledPage = lazy(() => import("../views/Payments/payment-bank-debit-schedule-page"));
const PaymentACHDebitSubmittedPage = lazy(() => import("../views/Payments/payment-ach-debit-submit-page"));
const PaymentOpenBankingSubmittedPage = lazy(() => import("../views/Payments/payment-open-banking-submit-page"));

const SessionExpiredPage = lazy(() => import("../views/session-expired/session-expired-page"));
const BankDebitSetupStatusPage = lazy(() => import("../views/Payments/BankDebitSetupStatusPage"));

const OpayoPiCardFramePage = lazy(() => import("../views/PaymentProviders/opayo/pi/iframe/opayo-pi-card-frame-page"));
const OpayoPi3dSecureRedirectPage = lazy(
  () => import("../views/PaymentProviders/opayo/pi/iframe/opayo-pi-3d-secure-reedirect-page")
);
const OpayoPi3dSecureFramePage = lazy(() => import("../views/PaymentProviders/opayo/pi/iframe/opayo-pi-3d-secure-frame-page"));

const TrueLayerRedirectPage = lazy(() => import("../views/PaymentProviders/true-layer/true-layer-redirect-page"));
const TotalProcessingRedirectPage = lazy(
  () => import("../views/PaymentProviders/total-processing/total-processing-redirect-page")
);

const Layout = (): ReactElement => {
  const pi = getProductInfo();
  return (
    <IntentToPayServiceProvider>
      <BrowserRouter>
        <Header />
        <Container fluid>
          <div>{pi.appVersion}</div>
          <div className='flex-container'>
            <div className='d-flex flex-column my-4 main-page'>
              <Suspense fallback={<LoadingPage />}>
                <Switch>
                  <Route key={RouteConsts.Health} path={RouteConsts.Health}>
                    <HealthPage />
                  </Route>
                  <Route path={RouteConsts.Home} exact>
                    <HomePage />
                  </Route>
                  <Route path={RouteConsts.Preview} exact>
                    <PreviewPage />
                  </Route>
                  <Route path={RouteConsts.Test} exact>
                    <TestPage />
                  </Route>
                  {/* <Route path='/styles' exact component={StyleGuidePage} /> */}
                  <Route path={`${RouteConsts.RequestLink}/:requestLinkId`}>
                    <RequestLinkPage />
                  </Route>
                  <Route path={RouteConsts.Session}>
                    <PaymentRequestPage />
                  </Route>
                  <Route path={`${RouteConsts.PaymentProcessing}/${TrueLayerProviderName}/:token`}>
                    <TrueLayerRedirectPage />
                  </Route>
                  <Route path={`${RouteConsts.PaymentProcessing}/${TotalProcessingProviderName}`}>
                    <TotalProcessingRedirectPage />
                  </Route>
                  <Route path={`${RouteConsts.PaymentProcessing}/:providerName`}>
                    <PaymentProcessingPage />
                  </Route>
                  <Route path={RouteConsts.PaymentProcessing}>
                    <PaymentProcessingPage />
                  </Route>
                  <Route path={RouteConsts.PaymentWaiting}>
                    <PaymentWaitingPage />
                  </Route>
                  <Route path={RouteConsts.PaymentReserved}>
                    <PaymentReservedPage />
                  </Route>
                  <Route path={RouteConsts.PaymentBankDebitScheduled}>
                    <PaymentBankDebitScheduledPage />
                  </Route>
                  <Route path={RouteConsts.PaymentACHDebitSubmitted}>
                    <PaymentACHDebitSubmittedPage />
                  </Route>
                  <Route path={RouteConsts.PaymentOpenBankingSubmitted}>
                    <PaymentOpenBankingSubmittedPage />
                  </Route>
                  <Route path={RouteConsts.PaymentRequestACHDebitProcessingPage}>
                    <PaymentRequestACHDebitProcessingPage />
                  </Route>
                  <Route path={RouteConsts.PaymentComplete}>
                    <PaymentCompletePage />
                  </Route>
                  <Route path={RouteConsts.OpayoEmbeddedCard}>
                    <OpayoPiCardFramePage />
                  </Route>
                  <Route path={RouteConsts.OpayoEmbedded3dSecure}>
                    <OpayoPi3dSecureFramePage />
                  </Route>
                  <Route path={RouteConsts.Opayo3dSecureRedirect}>
                    <OpayoPi3dSecureRedirectPage />
                  </Route>
                  <Route path={RouteConsts.SessionExpired}>
                    <SessionExpiredPage />
                  </Route>
                  <Route path={`${RouteConsts.DirectDebitSetupStatus}/:providerName/:status`}>
                    <BankDebitSetupStatusPage />
                  </Route>
                  <Route path={`${RouteConsts.Error}/:code`}>
                    <ErrorPage />
                  </Route>
                  <Route render={() => <Redirect to={`${RouteConsts.Error}/${HttpStatusCode.NotFound.toString()}`} />} />
                </Switch>
              </Suspense>
            </div>
          </div>
        </Container>
        <Footer />
      </BrowserRouter>
    </IntentToPayServiceProvider>
  );
};

export default Layout;
